// Field-marketing (rep tracking) DTOs — the contract the phone portal and the lab console share.
//
// Three ideas drive the shape of everything here:
//  1. A rep is tracked only while ON DUTY. A shift is opened with a live selfie and closed by the
//     rep (or auto-expired), so there is an explicit, auditable window of consent — we never hold a
//     trail for someone who is off the clock.
//  2. Position is EVIDENCE, not a form field. The client may only forward what the Geolocation API
//     handed it; the server stamps its own receive time, re-derives distance/speed and records an
//     integrity verdict. Nothing in the UI ever lets a human type a coordinate.
//  3. Stops are DERIVED, never declared. The server segments the trail into dwell periods, so
//     "where he stopped and for how long" is a property of the data, not of what the rep claims.
import { z } from 'zod';

// --- Tuning constants (shared so the phone and the server agree on what a stop is) ---------------

/** Two fixes within this radius of a stop's centre are the same stop. Roughly a building + parking. */
export const FIELD_STOP_RADIUS_M = 75;
/** A dwell shorter than this is passing traffic (a light, a jam), not a stop worth reporting. */
export const FIELD_MIN_STOP_SECONDS = 180;
/**
 * Standing inside a known clinic's geofence for this long counts as having ARRIVED: the stop is
 * named and the lab is told while the rep is still there, rather than after they leave. Shorter
 * than the stop threshold on purpose - two minutes at the door is a visit starting, not traffic.
 */
export const FIELD_ARRIVAL_SECONDS = 120;
/**
 * A rep on duty whose phone has sent nothing for this long is not being tracked - the app is in
 * the background or the screen is locked, and a browser stops delivering fixes there. The server
 * pushes a nudge so they open the app again; the nudge repeats no more often than
 * FIELD_NUDGE_REPEAT_SECONDS.
 */
export const FIELD_SILENCE_NUDGE_SECONDS = 300;
export const FIELD_NUDGE_REPEAT_SECONDS = 900;
/** Target seconds between fixes while on duty. Battery-aware, dense enough to segment stops. */
export const FIELD_PING_INTERVAL_SECONDS = 30;
/** A fix older than this when it reaches us is history, not a live position. */
export const FIELD_PING_MAX_AGE_SECONDS = 900;
/** Above this ground speed the pair of fixes cannot both be real (~300 km/h). */
export const FIELD_MAX_PLAUSIBLE_SPEED_MPS = 83;
/** Fixes vaguer than this are stored but never trusted to open or close a stop. */
export const FIELD_POOR_ACCURACY_M = 200;
/** A selfie must reach the server within this window of being taken — a gallery file cannot. */
export const FIELD_SELFIE_MAX_AGE_SECONDS = 180;
/** A shift left open longer than this is auto-closed; nobody works a 16-hour field day. */
export const FIELD_SHIFT_MAX_HOURS = 16;

// --- Core entities ------------------------------------------------------------------------------

export interface FieldRep {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  repCode: string;
  phone: string | null;
  territory: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type FieldShiftStatus = 'active' | 'ended' | 'expired';

export interface FieldShift {
  id: string;
  repId: string;
  status: FieldShiftStatus;
  startedAt: string;
  endedAt: string | null;
  startLat: number | null;
  startLng: number | null;
  endLat: number | null;
  endLng: number | null;
  startPhotoId: string | null;
  endPhotoId: string | null;
  /** Server-derived, from accepted fixes only. */
  distanceM: number;
  pingCount: number;
  /** Fixes the integrity check refused to trust. A rising count is the signal to look. */
  suspectCount: number;
  deviceLabel: string | null;
  /** When the rep was last pushed a "tracking paused" nudge for this shift. */
  nudgedAt: string | null;
  createdAt: string;
}

/** Why a fix was not trusted. Recorded per-ping so a dispute can be reconstructed later. */
export type FieldIntegrityReason =
  | 'mock_provider'
  | 'impossible_speed'
  | 'clock_skew'
  | 'stale_fix'
  | 'poor_accuracy'
  | 'no_accuracy'
  | 'frozen_position';

export interface FieldPing {
  id: string;
  repId: string;
  shiftId: string;
  /** Device clock at the moment of the fix. Kept, but never trusted for ordering. */
  at: string;
  /** Server clock when the fix arrived. This is what the timeline is built on. */
  receivedAt: string;
  lat: number;
  lng: number;
  accuracyM: number | null;
  altitudeM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  batteryPct: number | null;
  /** Metres from the previous accepted fix, computed server-side. */
  movedM: number | null;
  /** Ground speed implied by the previous accepted fix, computed server-side. */
  derivedSpeedMps: number | null;
  integrity: 'ok' | 'suspect';
  reasons: FieldIntegrityReason[];
}

export interface FieldStop {
  id: string;
  repId: string;
  shiftId: string;
  startedAt: string;
  endedAt: string;
  durationS: number;
  lat: number;
  lng: number;
  radiusM: number;
  pingCount: number;
  isOpen: boolean;
  /** Filled when the stop centre falls inside a known place's radius. */
  placeId: string | null;
  placeName: string | null;
  clinicId: string | null;
  /** Visits the rep logged while standing here. */
  visitCount: number;
  /** When the lab was told the rep had arrived at the clinic (null: never matched, or too brief). */
  arrivedNotifiedAt: string | null;
  /** When the lab was told the rep had left, with the time of stay. */
  departedNotifiedAt: string | null;
}

/** Payload of the `field.arrival` / `field.departure` live events the lab console listens to. */
export interface FieldPresenceEvent {
  kind: 'arrival' | 'departure';
  repId: string;
  repName: string;
  stopId: string;
  clinicId: string | null;
  placeId: string;
  placeName: string;
  at: string;
  /** Seconds the rep stayed - known only on departure. */
  durationS: number | null;
}

export type FieldVisitPurpose =
  | 'introduction'
  | 'follow_up'
  | 'delivery'
  | 'collection'
  | 'complaint'
  | 'payment'
  | 'other';

export type FieldVisitOutcome =
  | 'interested'
  | 'order_placed'
  | 'needs_follow_up'
  | 'not_interested'
  | 'no_meeting';

export const FIELD_VISIT_PURPOSES: readonly FieldVisitPurpose[] = [
  'introduction',
  'follow_up',
  'delivery',
  'collection',
  'complaint',
  'payment',
  'other',
];

export const FIELD_VISIT_OUTCOMES: readonly FieldVisitOutcome[] = [
  'interested',
  'order_placed',
  'needs_follow_up',
  'not_interested',
  'no_meeting',
];

export const FIELD_PURPOSE_LABELS: Record<FieldVisitPurpose, string> = {
  introduction: 'Introduction',
  follow_up: 'Follow-up',
  delivery: 'Delivery',
  collection: 'Collection',
  complaint: 'Complaint',
  payment: 'Payment',
  other: 'Other',
};

export const FIELD_OUTCOME_LABELS: Record<FieldVisitOutcome, string> = {
  interested: 'Interested',
  order_placed: 'Order placed',
  needs_follow_up: 'Needs follow-up',
  not_interested: 'Not interested',
  no_meeting: 'Could not meet',
};

export interface FieldVisit {
  id: string;
  repId: string;
  repName: string;
  shiftId: string;
  stopId: string | null;
  clinicId: string | null;
  clinicName: string;
  contactName: string | null;
  contactPhone: string | null;
  purpose: FieldVisitPurpose;
  outcome: FieldVisitOutcome;
  notes: string | null;
  photoId: string | null;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  /** Metres between where the visit was logged and the stop the rep was standing in. */
  distanceFromStopM: number | null;
  at: string;
  createdAt: string;
}

export type FieldPhotoKind = 'checkin' | 'checkout' | 'visit';

export interface FieldPhoto {
  id: string;
  repId: string;
  shiftId: string | null;
  kind: FieldPhotoKind;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  widthPx: number | null;
  heightPx: number | null;
  capturedAt: string;
  receivedAt: string;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
}

/** A place the lab knows about — used to name a stop ("stopped at Smile Dental, 41 min"). */
export interface FieldPlace {
  id: string;
  name: string;
  clinicId: string | null;
  lat: number;
  lng: number;
  radiusM: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// --- Read models --------------------------------------------------------------------------------

/** One row of the lab's live board. */
export interface FieldLiveRep {
  rep: FieldRep;
  shift: FieldShift | null;
  onDuty: boolean;
  lastFix: {
    at: string;
    receivedAt: string;
    lat: number;
    lng: number;
    accuracyM: number | null;
    batteryPct: number | null;
    integrity: 'ok' | 'suspect';
  } | null;
  /** Seconds since the last fix reached the server. Silence is the thing to notice. */
  secondsSinceFix: number | null;
  /** The stop they are standing in right now, if any. */
  currentStop: FieldStop | null;
  todayDistanceM: number;
  todayStops: number;
  todayVisits: number;
  todayStoppedS: number;
  /** Seconds stopped at a place the lab does not know, once that passes FIELD_IDLE_MIN_SECONDS. */
  idleS: number | null;
}

/** Everything about one rep on one day — the screen the lab actually reads. */
export interface FieldDay {
  rep: FieldRep;
  date: string;
  shifts: FieldShift[];
  stops: FieldStop[];
  visits: FieldVisit[];
  photos: FieldPhoto[];
  /** Decimated trail for drawing, ordered by receivedAt. */
  track: Array<{ lat: number; lng: number; at: string; integrity: 'ok' | 'suspect' }>;
  totals: {
    distanceM: number;
    stops: number;
    visits: number;
    onDutyS: number;
    stoppedS: number;
    movingS: number;
    suspectPings: number;
    firstFixAt: string | null;
    lastFixAt: string | null;
    /** Time stopped 15 min+ somewhere that is not a known clinic, and how many such stops. */
    idleS: number;
    idleStops: number;
  };
}

/** One rep's slice of a printable report: their days in the range that had any activity. */
export interface FieldReportRepSection {
  rep: FieldRep;
  totals: {
    onDutyS: number;
    distanceM: number;
    stops: number;
    visits: number;
    suspectPings: number;
    uniqueClinics: number;
  };
  /** Only days with a shift, a stop, or a visit - a day nobody worked earns no page. */
  days: FieldDay[];
}

/**
 * A printable report over a date range, one or every rep. Built server-side from the same
 * per-day figures the live screens use (`buildDay`), so a report can never disagree with what a
 * manager already saw on screen - it is the same numbers, just gathered across days and reps.
 */
export interface FieldReport {
  from: string;
  to: string;
  generatedAt: string;
  reps: FieldReportRepSection[];
}

/** The rep's own home screen. */
export interface FieldMe {
  rep: FieldRep;
  shift: FieldShift | null;
  today: {
    distanceM: number;
    stops: number;
    visits: number;
    onDutyS: number;
  };
  recentVisits: FieldVisit[];
  currentStop: FieldStop | null;
}

// --- Request schemas ----------------------------------------------------------------------------

/** Multipart text fields arrive as strings, so every numeric on those routes is coerced. */
const coordFields = {
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  accuracyM: z.coerce.number().min(0).max(100000).nullish(),
};

/** One raw fix exactly as the Geolocation API produced it. There is no hand-typed variant. */
export const fieldFixSchema = z.object({
  at: z.string().min(20).max(40),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().min(0).max(100000).nullable(),
  altitudeM: z.number().nullable().optional(),
  speedMps: z.number().min(0).max(1000).nullable().optional(),
  headingDeg: z.number().min(0).max(360).nullable().optional(),
  batteryPct: z.number().min(0).max(100).nullable().optional(),
  /** The device told us the position came from a mock provider. Honest clients say so. */
  mocked: z.boolean().optional(),
});
export type FieldFix = z.infer<typeof fieldFixSchema>;

/** Fixes are posted in batches so the phone can buffer through a tunnel and catch up. */
export const fieldPingBatchSchema = z.object({
  shiftId: z.string().min(1),
  fixes: z.array(fieldFixSchema).min(1).max(200),
});
export type FieldPingBatch = z.infer<typeof fieldPingBatchSchema>;

export interface FieldPingBatchResult {
  accepted: number;
  suspect: number;
  rejected: number;
  shift: FieldShift;
  currentStop: FieldStop | null;
}

/** Check-in. The selfie rides in the same multipart body as the fix it belongs to. */
export const fieldStartShiftSchema = z.object({
  ...coordFields,
  capturedAt: z.string().min(20).max(40),
  deviceLabel: z.string().trim().max(120).nullish(),
});
export type FieldStartShiftRequest = z.infer<typeof fieldStartShiftSchema>;

export const fieldEndShiftSchema = z.object({
  lat: z.coerce.number().min(-90).max(90).nullish(),
  lng: z.coerce.number().min(-180).max(180).nullish(),
  accuracyM: z.coerce.number().min(0).max(100000).nullish(),
  capturedAt: z.string().min(20).max(40).nullish(),
});
export type FieldEndShiftRequest = z.infer<typeof fieldEndShiftSchema>;

export const fieldVisitSchema = z.object({
  ...coordFields,
  capturedAt: z.string().min(20).max(40),
  clinicId: z.string().min(1).nullish(),
  clinicName: z.string().trim().min(2).max(160),
  contactName: z.string().trim().max(120).nullish(),
  contactPhone: z.string().trim().max(40).nullish(),
  purpose: z.enum([
    'introduction',
    'follow_up',
    'delivery',
    'collection',
    'complaint',
    'payment',
    'other',
  ]),
  outcome: z.enum([
    'interested',
    'order_placed',
    'needs_follow_up',
    'not_interested',
    'no_meeting',
  ]),
  notes: z.string().trim().max(2000).nullish(),
});
export type FieldVisitRequest = z.infer<typeof fieldVisitSchema>;

export const fieldPlaceSchema = z.object({
  name: z.string().trim().min(2).max(160),
  clinicId: z.string().min(1).nullish(),
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  radiusM: z.coerce.number().min(20).max(5000),
  isActive: z.boolean().optional(),
});
export type FieldPlaceRequest = z.infer<typeof fieldPlaceSchema>;

export const fieldRepUpdateSchema = z
  .object({
    phone: z.string().trim().max(40).nullish(),
    territory: z.string().trim().max(160).nullish(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type FieldRepUpdateRequest = z.infer<typeof fieldRepUpdateSchema>;

// --- Presentation helpers (shared so phone and console phrase durations identically) -------------

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export function formatDistance(metres: number): string {
  if (!Number.isFinite(metres)) return '-';
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(metres < 10000 ? 2 : 1)} km`;
}

export const FIELD_INTEGRITY_LABELS: Record<FieldIntegrityReason, string> = {
  mock_provider: 'Device reported a mock location provider',
  impossible_speed: 'Jumped further than physically possible since the last fix',
  clock_skew: 'Device clock disagrees with the server',
  stale_fix: 'Fix was already old when it arrived',
  poor_accuracy: 'Accuracy too vague to place',
  no_accuracy: 'No accuracy reported',
  frozen_position: 'Identical coordinates repeated exactly',
};

/** Great-circle distance in metres. Used identically on both sides so numbers never disagree. */
export function haversineM(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 6371008.8; // mean Earth radius, metres
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// --- Analytics, coverage and alerts ------------------------------------------------------------

/** A stop this long at a location that is not a known clinic counts as idle time. */
export const FIELD_IDLE_MIN_SECONDS = 900;
/** Phone battery at or below this on a fix raises a one-off alert to managers for that shift. */
export const FIELD_BATTERY_ALERT_PCT = 15;

/**
 * How often a clinic of each class should be called on, in days. `null` = not expected at all.
 * `default` covers a clinic nobody has classified yet.
 */
export const CLINIC_VISIT_CADENCE_DAYS: Record<string, number | null> = {
  a: 14,
  strategic: 14,
  new: 7,
  growth: 21,
  b: 30,
  c: 60,
  inactive: null,
  default: 30,
};

export interface FieldRepStats {
  rep: FieldRep;
  rank: number;
  daysOnDuty: number;
  onDutyS: number;
  distanceM: number;
  stops: number;
  clinicStops: number;
  idleStops: number;
  idleS: number;
  visits: number;
  uniqueClinics: number;
  visitsPerDay: number;
  planned: number;
  planDone: number;
  planMissed: number;
  adherencePct: number | null;
  suspectPings: number;
  /** Average check-in time, HH:MM in the lab's timezone. */
  usualStart: string | null;
}

export interface FieldDailyPoint {
  date: string;
  onDutyS: number;
  distanceM: number;
  visits: number;
  stops: number;
  idleS: number;
  repsOnDuty: number;
}

export interface FieldAnalytics {
  from: string;
  to: string;
  days: number;
  reps: FieldRepStats[];
  daily: FieldDailyPoint[];
  totals: {
    onDutyS: number;
    distanceM: number;
    visits: number;
    stops: number;
    idleS: number;
    planned: number;
    planDone: number;
    suspectPings: number;
    uniqueClinics: number;
    adherencePct: number | null;
  };
}

export type FieldCoverageStatus = 'ok' | 'due' | 'overdue' | 'never' | 'excluded';

export interface FieldCoverageRow {
  clinicId: string;
  name: string;
  city: string | null;
  classification: string | null;
  potential: string | null;
  repId: string | null;
  repName: string | null;
  territoryName: string | null;
  hasPin: boolean;
  lastVisitAt: string | null;
  lastStayAt: string | null;
  /** The later of the last logged visit and the last geofence stay. */
  lastTouchAt: string | null;
  daysSince: number | null;
  cadenceDays: number | null;
  status: FieldCoverageStatus;
  visits30: number;
  visits90: number;
  stays90: number;
}

export interface FieldCoverage {
  asOf: string;
  rows: FieldCoverageRow[];
  summary: { total: number; ok: number; due: number; overdue: number; never: number; excluded: number; coveragePct: number | null };
}

/** A manager-facing alert raised from a rep's fixes (SSE `field.alert`). */
export interface FieldAlertEvent {
  kind: 'battery_low' | 'gps_suspect';
  repId: string;
  repName: string;
  shiftId: string;
  at: string;
  detail: string;
}
